import { addDays, addHours, addMonths, isSameDay, nextMonday, setHours, startOfDay, startOfHour } from 'date-fns';

/** Presets land at the start of a workday so a ticket or message arrives when someone is there to act on it. */
export const SUPPORT_WORKDAY_START_HOUR = 8;

/** Matches the API's snooze limit; past a year the desk queue is the wrong place for the reminder. */
export const SUPPORT_SCHEDULE_DAYS_MAX = 366;

export type SupportTimePresetId = 'laterToday' | 'tomorrow' | 'nextMonday' | 'nextWeek' | 'nextMonth';

export interface SupportTimePreset {
	id: SupportTimePresetId;
	label: string;
	at: Date;
}

const atWorkdayStart = (day: Date) => setHours(startOfDay(day), SUPPORT_WORKDAY_START_HOUR);

/** Ordered soonest first; a preset that lands on the same instant as an earlier one is dropped. */
export function getSupportTimePresets(now: Date): SupportTimePreset[] {
	const laterToday = startOfHour(addHours(now, 3));
	const candidates: SupportTimePreset[] = [
		...(isSameDay(laterToday, now) ? [{ id: 'laterToday' as const, label: 'Later today', at: laterToday }] : []),
		{ id: 'tomorrow', label: 'Tomorrow', at: atWorkdayStart(addDays(now, 1)) },
		{ id: 'nextMonday', label: 'Next Monday', at: atWorkdayStart(nextMonday(now)) },
		{ id: 'nextWeek', label: 'In a week', at: atWorkdayStart(addDays(now, 7)) },
		{ id: 'nextMonth', label: 'In a month', at: atWorkdayStart(addMonths(now, 1)) },
	];

	const seen = new Set<number>();
	return candidates.filter(preset => {
		const time = preset.at.getTime();
		if (seen.has(time)) return false;
		seen.add(time);
		return true;
	});
}

/** Local wall-clock `day` at `hhmm` (24h `HH:mm`); null when the time is not a valid `HH:mm`. */
export function combineDayAndTime(day: Date, hhmm: string): Date | null {
	const match = /^(\d{2}):(\d{2})$/.exec(hhmm);
	if (!match) return null;
	const hours = Number(match[1]);
	const minutes = Number(match[2]);
	if (hours > 23 || minutes > 59) return null;
	const combined = startOfDay(day);
	combined.setHours(hours, minutes, 0, 0);
	return combined;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Exact elapsed days, like the API's check; a calendar-day add would drift an hour across a DST change. */
export function getSupportTimeLimit(now: Date): Date {
	return new Date(now.getTime() + SUPPORT_SCHEDULE_DAYS_MAX * DAY_MS);
}

/** Why `at` cannot be used, or null when it can. */
export function getSupportTimeError(at: Date | null, now: Date): string | null {
	if (!at || Number.isNaN(at.getTime())) return 'Pick a date and time.';
	if (at.getTime() <= now.getTime()) return 'Pick a time in the future.';
	if (at.getTime() > getSupportTimeLimit(now).getTime()) return 'Pick a time within the next year.';
	return null;
}

/** "Wed 8:00 AM" within the coming week, "Nov 22, 8:00 AM" otherwise, past times included. */
export function formatSupportTime(at: Date, now: Date): string {
	// A bare weekday reads as upcoming, so a time already passed (a late wake job) needs the date.
	const deltaMs = at.getTime() - now.getTime();
	const withinWeek = deltaMs >= 0 && deltaMs < 6 * DAY_MS;
	return at.toLocaleString(
		undefined,
		withinWeek
			? { weekday: 'short', hour: 'numeric', minute: '2-digit' }
			: { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
	);
}
